import {Action} from '@reduxjs/toolkit'
import {persistReducer} from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import {put, takeLatest} from 'redux-saga/effects'
import {UserModel} from '../../../shared/models'
import {getUserByToken} from './AuthCRUD'

export interface ActionWithPayload<T> extends Action {
    payload?: T
}

export const actionTypes = {
    Login: '[Login] Action',
    Logout: '[Logout] Action',
    UserRequested: '[Request User] Action',
    UserLoaded: '[Load User] Auth API',
    SetUser: '[Set User] Action',
    refresh_token: '[Refresh Token] Action',
    clearToken: '[Clear Token] Action',
    permissionPages: '[Permission Pages] Action',
    setRepresentative: '[Set Representative] Action',
}

const initialAuthState: IAuthState = {
    user: undefined,
    token: undefined,
    refresh_token: undefined,
    representative_id: undefined,
    permission_pages: undefined,
}

export interface IAuthState {
    user?: UserModel
    token?: string
    refresh_token?: string
    representative_id?: number
    permission_pages?: number[]
}

export const reducer = persistReducer(
    {storage, key: 'v100-demo1-auth', whitelist: ['user', 'token', 'refresh_token', 'representative_id']},
    (state: IAuthState = initialAuthState, action: ActionWithPayload<IAuthState>) => {
        switch (action.type) {
            case actionTypes.Login: {
                const token = action.payload?.token
                const refresh_token = action.payload?.refresh_token
                const user = action.payload?.user
                const representative_id = user?.user_representatives?.[0]?.representative?.id
                return {token, user: user, refresh_token, representative_id}
            }

            case actionTypes.Logout: {
                return initialAuthState
            }

            case actionTypes.UserRequested: {
                return {...state, user: undefined}
            }

            case actionTypes.UserLoaded: {
                const user = action.payload?.user
                return {...state, user}
            }

            case actionTypes.SetUser: {
                const user = action.payload?.user
                return {...state, user}
            }

            case actionTypes.refresh_token: {
                const token = action.payload?.token
                const refresh_token = action.payload?.refresh_token
                const representative_id = action.payload?.representative_id
                return {
                    token,
                    refresh_token,
                    representative_id,
                    user: undefined,
                }
            }

            case actionTypes.clearToken: {
                return {
                    token: undefined,
                    refresh_token: undefined,
                    user: undefined,
                }
            }

            case actionTypes.setRepresentative: {
                const representative_id = action.payload?.representative_id;
                return {...state, representative_id}
            }

            case actionTypes.permissionPages: {
                const permission_pages = action.payload?.permission_pages
                return {...state, permission_pages}
            }

            default:
                return state
        }
    }
)

export const actions = {
    login: (token: string, refresh_token: string, user: UserModel, pvdId: number) => ({
        type: actionTypes.Login,
        payload: {token, refresh_token, user, pvdId},
    }),
    logout: () => ({type: actionTypes.Logout}),
    requestUser: () => ({
        type: actionTypes.UserRequested,
    }),
    fulfillUser: (user: UserModel) => ({type: actionTypes.UserLoaded, payload: {user: user}}),
    setUser: (user?: UserModel) => ({type: actionTypes.SetUser, payload: {user: user}}),
    refresh_token: (token: string, refresh_token: string) => ({
        type: actionTypes.refresh_token,
        payload: {token, refresh_token},
    }),
    clearToken: () => ({
        type: actionTypes.clearToken,
    }),
    permissionPages: (permission_pages: number[]) => ({
        type: actionTypes.permissionPages,
        payload: {permission_pages: permission_pages},
    }),
    setRepresentative: (representative_id: number) => ({
        type: actionTypes.setRepresentative,
        payload: {representative_id},
    }),
}

export function* saga() {
    yield takeLatest(actionTypes.Login, function* loginSaga() {
        yield put(actions.requestUser())
    })

    yield takeLatest(actionTypes.UserRequested, function* userRequested() {
        const {data} = yield getUserByToken(0)
        yield put(actions.fulfillUser(data.data.user))
    })
}
